import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import TypeOfProgramApiService from '../../../../api/TypeOfProgramApiService';
import AuthorizedContent from '../../../../components/AuthorizedContent';
import TypeOfProgramDetailsForm from '../../../../components/forms/postSecondary/TypeOfProgramDetailsForm';
import EditCancelButton from '../../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../../config/Breadcrumbs';
import Routes from '../../../../config/Routes';
import FeatureFlag from '../../../../consts/FeatureFlag';
import * as DeleteTypeOfProgramHandler from '../../../../handlerModels/DeleteTypeOfProgramHandler';
import * as GetTypeOfProgramDetailsHandler from '../../../../handlerModels/GetTypeOfProgramDetailsHandler';
import TypeOfProgramDTO from '../../../../models/TypeOfProgramDTO';
import HeaderPortal from '../../../../portals/HeaderPortal';
import BaseFormState from '../../../../redux/bases/BaseFormState';
import HistoryUtil from '../../../../utils/HistoryUtil';
import PageStayPrompt from '../../../../utils/PageStayPrompt';
import ParameterUtil from '../../../../utils/ParameterUtil';

interface TypeOfProgramDetailsPageState extends BaseFormState {
  typeOfProgramId: number;
  typeOfProgram?: TypeOfProgramDTO;
}

interface TypeOfProgramDetailsPageProps {
}

class TypeOfProgramDetailsPage extends React.Component<TypeOfProgramDetailsPageProps & RouteComponentProps<RouteObject>, TypeOfProgramDetailsPageState> {
  private readonly _detailsRef = React.createRef<TypeOfProgramDetailsForm>();

  constructor(props: TypeOfProgramDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      typeOfProgramId: 0
    };
  }

  componentDidMount() {
    const typeOfProgramId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (typeOfProgramId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false })
    }
    else {
      const typeOfProgramIdNumber = Number.parseInt(typeOfProgramId);
      this.loadTypeOfProgram(typeOfProgramIdNumber);
    }
  }

  private loadTypeOfProgram = (typeOfProgramId: number) => {
    this.setState({ typeOfProgramId: typeOfProgramId });

    TypeOfProgramApiService.getTypeOfProgramDetails(typeOfProgramId)
      .then((results: GetTypeOfProgramDetailsHandler.Result) => {
        if (results.typeOfProgram) {
          this.setState({ typeOfProgram: results.typeOfProgram });
        }
      }).catch(() => {
        this.setState({ error: true });
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._detailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private specialEmphasisDetailsSaved = (TypeOfProgramId: number) => {
    this.setState({ isEditing: false });
    if (TypeOfProgramId != this.state.typeOfProgramId) {
      HistoryUtil.replace(Routes.generate(Routes.TYPE_OF_PROGRAM_DETAILS, { id: TypeOfProgramId }));
    }
    else {
      this.loadTypeOfProgram(TypeOfProgramId);
    }
  }

  private deleteConfirmation = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this Type Of Program?',
      okText: 'Delete',
      onOk: () => this.deleteTypeOfProgram(id)
    });
  }

  private deleteTypeOfProgram = (typeOfProgramId: number) => {
    this.setState({ submitting: true });

    const request = DeleteTypeOfProgramHandler.Request.create({
      typeOfProgramId: typeOfProgramId,
    });

    TypeOfProgramApiService.deleteTypeOfProgram(request)
      .then((result: DeleteTypeOfProgramHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('Type of program was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Type of program could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToTypeOfPrograms();
      });
  }

  private goToTypeOfPrograms = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.typeOfProgramId == 0 ? 'New Type Of Program' : this.state.typeOfProgram?.name ?? '...';

    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToTypeOfPrograms}
          breadcrumbs={Breadcrumbs.typeOfProgramDetails(title, this.state.typeOfProgramId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content>
    );
  }

  renderDetailsCard() {
    const editButton = this.state.typeOfProgramId == 0 ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_TYPE_OF_PROGRAM]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton}>
        <TypeOfProgramDetailsForm
          ref={this._detailsRef}
          typeOfProgramId={this.state.typeOfProgramId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.specialEmphasisDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete Type Of Progream' key={this.state.typeOfProgramId} onClick={() => this.deleteConfirmation(this.state.typeOfProgramId)}  >
          Delete Type Of Progream
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_TYPE_OF_PROGRAM]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(TypeOfProgramDetailsPage);
